import { DEFAULT_AGENT_ID, useChatStore } from '../../store/chatStore';
import { useSettingsStore, type ThemeMode } from '../../store/settingsStore';
import { PinIcon, PinOffIcon } from '../ui/Icons';
import type { RateLimitWindow } from '../../protocol/types';

// Provider usage snapshots stop being meaningful once the agent goes quiet —
// hide the bars after an hour with no refresh (matches the host re-send rule).
const RATE_LIMIT_MAX_AGE_MS = 60 * 60 * 1000;

function windowLabel(minutes: number): string {
  if (minutes >= 10080) return 'Woche';
  if (minutes >= 60) return `${Math.round(minutes / 60)}h`;
  return `${minutes}m`;
}

function formatReset(resetsAtSec: number): string {
  if (!resetsAtSec) return 'unbekannt';
  return new Date(resetsAtSec * 1000).toLocaleString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function UsageBar({ window: w }: { window: RateLimitWindow }) {
  const pct = Math.max(0, Math.min(100, Math.round(w.usedPercent)));
  const label = windowLabel(w.windowMinutes);
  return (
    <div
      className="h-1 w-full overflow-hidden rounded-full bg-divider"
      title={`${label}: ${pct}% genutzt · Reset ${formatReset(w.resetsAt)}`}
    >
      <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
    </div>
  );
}

// Usage bars for the active agent, in their own section above the Themes
// divider. Subscribes narrowly to the active agent's snapshot. Renders the
// whole section (incl. its top divider) only when there's fresh data, so a
// Claude agent or a quiet codex agent leaves no empty band.
function ActiveAgentRateLimit() {
  const rl = useChatStore((s) => s.chats[s.activeAgentId]?.rateLimit ?? null);
  if (!rl) return null;
  if (Date.now() - rl.updatedAt > RATE_LIMIT_MAX_AGE_MS) return null;
  if (!rl.primary && !rl.secondary) return null;
  return (
    <div className="border-t border-divider p-2">
      <div className="flex flex-col gap-1.5 px-1.5 py-1">
        {rl.primary ? <UsageBar window={rl.primary} /> : null}
        {rl.secondary ? <UsageBar window={rl.secondary} /> : null}
      </div>
    </div>
  );
}

// Theme selection is presented as (family × variant), but persisted as a flat
// ThemeMode so localStorage stays compatible. Default has three variants
// because System only makes sense on the OS-following palette; the explicit
// palettes (Everforest, Espresso) just toggle light/dark.

type ThemeFamily = 'default' | 'everforest' | 'espresso' | 'lavendel';
type ThemeVariant = 'system' | 'light' | 'dark';

const FAMILIES: {
  id: ThemeFamily;
  label: string;
  swatch: string;
  variants: ThemeVariant[];
}[] = [
  {
    id: 'default',
    // Neutral light/dark split — the only family whose identity colour
    // depends on which variant is active (incl. System).
    label: 'Default',
    swatch: 'linear-gradient(135deg,#f4f6fa 0 50%,#14161d 50% 100%)',
    variants: ['system', 'light', 'dark'],
  },
  {
    id: 'everforest',
    label: 'Everforest',
    swatch: '#a7c080',
    variants: ['light', 'dark'],
  },
  {
    id: 'espresso',
    label: 'Espresso',
    swatch: '#f08d71',
    variants: ['light', 'dark'],
  },
  {
    id: 'lavendel',
    label: 'Lavendel',
    swatch: 'rgb(200 135 168)',
    variants: ['light', 'dark'],
  },
];

const VARIANT_LABELS: Record<ThemeVariant, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};

function decompose(mode: ThemeMode): { family: ThemeFamily; variant: ThemeVariant } {
  switch (mode) {
    case 'system':
      return { family: 'default', variant: 'system' };
    case 'light':
      return { family: 'default', variant: 'light' };
    case 'dark':
      return { family: 'default', variant: 'dark' };
    case 'everforest':
      return { family: 'everforest', variant: 'dark' };
    case 'everforest-light':
      return { family: 'everforest', variant: 'light' };
    case 'espresso':
      return { family: 'espresso', variant: 'dark' };
    case 'espresso-light':
      return { family: 'espresso', variant: 'light' };
    case 'lavendel':
      return { family: 'lavendel', variant: 'light' };
    case 'lavendel-dark':
      return { family: 'lavendel', variant: 'dark' };
  }
}

function compose(family: ThemeFamily, variant: ThemeVariant): ThemeMode {
  if (family === 'default') return variant;
  if (family === 'everforest') return variant === 'light' ? 'everforest-light' : 'everforest';
  if (family === 'espresso') return variant === 'light' ? 'espresso-light' : 'espresso';
  return variant === 'light' ? 'lavendel' : 'lavendel-dark';
}

export function Sidebar({
  onPickAgent,
  width,
}: {
  onPickAgent?: () => void;
  /** Override width in px. Defaults to w-64 (16rem) when undefined. */
  width?: number;
}) {
  const agents = useChatStore((s) => s.agents);
  const activeId = useChatStore((s) => s.activeAgentId);
  const unread = useChatStore((s) => s.unread);
  const setActive = useChatStore((s) => s.setActiveAgent);
  const themeMode = useSettingsStore((s) => s.themeMode);
  const setThemeMode = useSettingsStore((s) => s.setThemeMode);
  const sidebarPinned = useSettingsStore((s) => s.sidebarPinned);
  const setSidebarPinned = useSettingsStore((s) => s.setSidebarPinned);

  const items = agents.length === 0 ? [{ id: DEFAULT_AGENT_ID, name: 'Messages' }] : agents;
  const activeAgent = agents.find((a) => a.id === activeId);
  const headerName = activeAgent?.name ?? (agents.length === 0 ? 'Niu' : 'Messages');
  const headerInitial = headerName.slice(0, 1).toUpperCase();
  const active = decompose(themeMode);

  return (
    <aside
      className="flex h-full flex-col border-r border-divider bg-surface"
      style={width != null ? { width: `${width}px` } : { width: '16rem' }}
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-divider px-4">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent text-xs font-bold text-white">
          {headerInitial}
        </span>
        <span className="flex-1 truncate text-[15px] font-semibold text-text-primary">
          {headerName}
        </span>
        <button
          type="button"
          onClick={() => setSidebarPinned(!sidebarPinned)}
          aria-label={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          aria-pressed={sidebarPinned}
          title={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'}
          className={`hidden h-7 w-7 items-center justify-center rounded-md transition-colors md:flex ${
            sidebarPinned
              ? 'bg-accent-soft text-accent'
              : 'text-text-secondary hover:bg-divider/40 hover:text-text-primary'
          }`}
        >
          {sidebarPinned ? <PinOffIcon size={14} /> : <PinIcon size={14} />}
        </button>
      </div>

      <nav className="scrollbar-thin flex-1 overflow-y-auto p-2">
        {items.map((a) => {
          const isActive = a.id === activeId;
          const count = unread[a.id] ?? 0;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                setActive(a.id);
                onPickAgent?.();
              }}
              className={`mb-1 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                isActive ? 'bg-accent-soft text-accent' : 'text-text-primary hover:bg-divider/40'
              }`}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                  isActive ? 'bg-accent text-white' : 'bg-elevated text-text-secondary'
                }`}
              >
                {a.name.slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{a.name}</span>
              {count > 0 ? (
                <span className="rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {count > 99 ? '99+' : count}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <ActiveAgentRateLimit />

      <div className="border-t border-divider p-2">
        <div className="px-1.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-faint">
          Theme
        </div>
        <div className="space-y-1">
          {FAMILIES.map((fam) => (
            <div key={fam.id} className="flex items-center gap-2 px-1.5 py-0.5">
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-full border border-divider"
                style={{ background: fam.swatch }}
              />
              <span className="flex-1 truncate text-sm text-text-secondary">{fam.label}</span>
              <div
                role="group"
                aria-label={`${fam.label} variant`}
                className="flex shrink-0 overflow-hidden rounded-md border border-divider bg-elevated"
              >
                {fam.variants.map((v) => {
                  const isActive = active.family === fam.id && active.variant === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setThemeMode(compose(fam.id, v))}
                      aria-pressed={isActive}
                      className={`px-2 py-0.5 text-[11px] font-medium transition-colors ${
                        isActive
                          ? 'bg-accent text-white'
                          : 'text-text-secondary hover:bg-divider/40 hover:text-text-primary'
                      }`}
                    >
                      {VARIANT_LABELS[v]}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}
